import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import { type ToolCardViewProps } from "~/features/blocks/inventory/tool-cards/card";
import { takeFirst } from "~/features/app/utils";
import { type Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
const COLS = 3;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}
export type ToolCardsBlockViewProps<TCard = ToolCardViewProps> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  getCardKey?: (card: TCard, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  mode?: string | null;
  renderCard: (card: TCard, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function ToolCardsBlockView<TCard = ToolCardViewProps>({
  anchor,
  cards = [],
  getCardKey,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  mode = "light",
  renderCard,
  sectionProps,
  theme = "white"
}: ToolCardsBlockViewProps<TCard>) {
  const allCards = cards ?? [];
  const firstRowCards = headline ? allCards.slice(0, COLS - 1) : allCards.slice(0, COLS);
  const remainingCards = headline ? allCards.slice(COLS - 1) : allCards.slice(COLS);
  const rows = chunk(remainingCards, COLS);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)",
    "paddingBottom": "var(--spacing-xl)",
    "@media (min-width: 1024px)": {
      "paddingBottom": "0"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <div>
          <_StyledDiv>
            <_StyledDiv2 $_css2={[{
            "display": "grid",
            "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
            "gap": "var(--spacing-sm)",
            "@media (min-width: 1024px)": {
              "gridTemplateColumns": "repeat(3, minmax(0, 1fr))",
              "gap": "var(--spacing-md)",
              "paddingLeft": "var(--spacing-xl)",
              "paddingRight": "var(--spacing-xl)",
              "paddingTop": "var(--spacing-xl)",
              "paddingBottom": "var(--spacing-xl)"
            }
          }, rows.length > 0 && {
            "@media (min-width: 1024px)": {
              "borderBottomWidth": "1px",
              "borderColor": "var(--stroke-primary)"
            }
          }]}>
              {headline && <_StyledDiv3>
                  <_StyledH>{headline}</_StyledH>
                </_StyledDiv3>}
              {firstRowCards.map((card, idx) => <_StyledDiv4 key={getCardKey?.(card, idx) || idx} $_css3={[{
              "paddingLeft": "var(--spacing-md)",
              "paddingRight": "var(--spacing-md)",
              "@media (min-width: 1024px)": {
                "paddingLeft": "var(--spacing-xl)",
                "paddingRight": "var(--spacing-xl)"
              }
            }]}>
                  {renderCard(card, idx)}
                </_StyledDiv4>)}
            </_StyledDiv2>
            {rows.map((row, rowIdx) => <_StyledDiv5 key={rowIdx} $_css4={[{
            "display": "grid",
            "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
            "gap": "var(--spacing-sm)",
            "@media (min-width: 1024px)": {
              "gridTemplateColumns": "repeat(3, minmax(0, 1fr))",
              "gap": "var(--spacing-md)",
              "paddingLeft": "var(--spacing-xl)",
              "paddingRight": "var(--spacing-xl)",
              "paddingTop": "var(--spacing-xl)",
              "paddingBottom": "var(--spacing-xl)"
            }
          }, rowIdx < rows.length - 1 && {
            "@media (min-width: 1024px)": {
              "borderBottomWidth": "1px",
              "borderColor": "var(--stroke-primary)"
            }
          }]}>
                {row.map((card, idx) => <_StyledDiv6 key={getCardKey?.(card, idx) || idx} $_css5={[{
              "paddingLeft": "var(--spacing-md)",
              "paddingRight": "var(--spacing-md)",
              "@media (min-width: 1024px)": {
                "paddingLeft": "var(--spacing-xl)",
                "paddingRight": "var(--spacing-xl)"
              }
            }]}>
                    {renderCard(card, idx)}
                  </_StyledDiv6>)}
              </_StyledDiv5>)}
          </_StyledDiv>
        </div>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1twdw6u-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1twdw6u-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1twdw6u-2"
})({
  "display": "flex",
  "flexDirection": "column"
});
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1twdw6u-3"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1twdw6u-4"
})({
  "display": "flex",
  "alignItems": "center",
  "padding": "var(--spacing-xl)"
});
var _StyledH = _styled6("h2").withConfig({
  componentId: "sc-sf-1twdw6u-5"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "color": "var(--text-primary)",
  "@media (min-width: 1024px)": {
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-1twdw6u-6"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-1twdw6u-7"
})`${p => p.$_css4}`;
var _StyledDiv6 = /*#__PURE__*/_styled9("div").withConfig({
  displayName: "tool-cards___StyledDiv6",
  componentId: "sc-10dousk-0"
})(["", ""], p => p.$_css5);